'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import {
  dispatchKitchenTransfer,
  fetchPeerKitchens,
  fetchTodayKitchenTransfers,
  newTransferCode,
  receiveKitchenTransfer,
  saveKitchenTransfer,
  scanKitchenTransferQr,
  fetchKitchenMaterialStock,
  type ApiKitchenTransfer,
  type ApiKitchenTransferLine,
  type KitchenTransferLineInput,
} from '@/features/kitchen/api/kitchenTransferApi'
import { fetchKitchenDelmans, fetchKitchenStockLots } from '@/features/kitchen/api/getDispatchOptions'
import { fetchTodayProductionPlan } from '@/features/kitchen/api/getTodayProduction'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { CollapsibleCategoryBlock } from '@/features/kitchen/CollapsibleCategoryBlock'
import {
  buildPlanCatalogFromTodayPlan,
  groupProductLinesByCategory,
  type PlanCatalogItem,
} from '@/features/kitchen/productionPlanLayout'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QrCameraScanner } from '@/shared/components/QrCameraScanner'
import { ActionSummaryCard } from '@/shared/components/ActionSummaryCard'
import { EmptyState } from '@/shared/components/EmptyState'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'

type Tab = 'send' | 'receive'
type SendView = 'list' | 'edit' | 'review'
type ReceiveView = 'list' | 'scan' | 'confirm'

type StockLot = {
  productName: string
  productionDate: string
  stockAvailable: number
}

type SendItemTab = 'product' | 'material'

type MaterialStockLot = {
  materialName: string
  uom: string
  stockAvailable: number
}

type EditorLine = KitchenTransferLineInput & { maxQty: number }

type ReceiveLineState = {
  itemType: 'product' | 'material'
  productName?: string
  materialName?: string
  productionDate?: string
  uom?: string
  planned: number
  qtyGood: number
}

const MOCK_PLAN_CATALOG: PlanCatalogItem[] = [
  { sku: 'Roti coklat', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Roti keju', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Donat gula', plan: 0, categoryName: 'Roti Manis', categorySortOrder: 1 },
  { sku: 'Croissant', plan: 0, categoryName: 'Pastry', categorySortOrder: 2 },
]

const SEND_FLOW_STEPS = kitchenTerms.kitchenTransfer.flowSteps.send.map((label) => ({ label }))
const RECEIVE_FLOW_STEPS = kitchenTerms.kitchenTransfer.flowSteps.receive.map((label) => ({ label }))

function sendFlowStep(view: SendView): number {
  if (view === 'review') return 3
  if (view === 'edit') return 2
  return 1
}

function receiveFlowStep(view: ReceiveView): number {
  if (view === 'confirm') return 3
  if (view === 'scan') return 2
  return 1
}

function transferStatusLabel(status: string): string {
  if (status === 'draft') return kitchenTerms.kitchenTransfer.status.draft
  if (status === 'in_transit') return kitchenTerms.kitchenTransfer.status.inTransit
  if (status === 'received') return kitchenTerms.kitchenTransfer.status.received
  return status
}

function transferStatusBadge(status: string): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'in_transit') return 'badge-warning'
  if (status === 'received') return 'badge-success'
  return 'badge-draft'
}

function formatProductionDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(new Date(`${iso.slice(0, 10)}T12:00:00`))
  } catch {
    return iso
  }
}

function formatTransferSummary(lines: ApiKitchenTransferLine[]): string {
  const totalQty = lines.reduce((sum, line) => sum + line.qty, 0)
  const productCount = lines.filter((l) => (l.item_type ?? 'product') === 'product').length
  const materialCount = lines.filter((l) => l.item_type === 'material').length
  const parts: string[] = []
  if (productCount > 0) parts.push(kitchenTerms.kitchenTransfer.summary.productCount(productCount))
  if (materialCount > 0) parts.push(kitchenTerms.kitchenTransfer.summary.materialCount(materialCount))
  return kitchenTerms.kitchenTransfer.summary.join(parts, totalQty)
}

function stockSummaryText(lots: StockLot[]): string {
  const available = lots.filter((lot) => lot.stockAvailable > 0)
  if (available.length === 0) return kitchenTerms.kitchenTransfer.stock.none
  const preview = available
    .slice(0, 3)
    .map((lot) => `${lot.productName} ${lot.stockAvailable}`)
    .join(' · ')
  const more =
    available.length > 3
      ? kitchenTerms.kitchenTransfer.stock.previewMore(available.length - 3)
      : ''
  return preview + more
}

function filterEditableLines<T extends { maxQty: number; qty: number }>(lines: T[], showAll: boolean): T[] {
  if (showAll) return lines
  const withStock = lines.filter((line) => line.maxQty > 0 || line.qty > 0)
  return withStock.length > 0 ? withStock : lines.filter((line) => line.qty > 0)
}

export function KitchenTransferPage({ initialTab = 'send' }: { initialTab?: Tab }) {
  const device = getKitchenDevice()
  const useApi = shouldUseKitchenApi()
  const kitchenName = device?.kitchenName ?? 'Dapur'

  const [tab, setTab] = useState<Tab>(initialTab)
  const [sendView, setSendView] = useState<SendView>('list')
  const [receiveView, setReceiveView] = useState<ReceiveView>('list')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [planCatalog, setPlanCatalog] = useState<PlanCatalogItem[]>(
    useApi ? [] : MOCK_PLAN_CATALOG,
  )
  const [outgoing, setOutgoing] = useState<ApiKitchenTransfer[]>([])
  const [incoming, setIncoming] = useState<ApiKitchenTransfer[]>([])
  const [peerKitchens, setPeerKitchens] = useState<{ id: string; name: string }[]>([])
  const [delmans, setDelmans] = useState<string[]>([])
  const [stockLots, setStockLots] = useState<StockLot[]>([])
  const [materialStock, setMaterialStock] = useState<MaterialStockLot[]>([])
  const [sendItemTab, setSendItemTab] = useState<SendItemTab>('product')
  const [showAllSendLines, setShowAllSendLines] = useState(false)

  const [transferCode, setTransferCode] = useState('')
  const [targetKitchen, setTargetKitchen] = useState('')
  const [delmanName, setDelmanName] = useState('')
  const [productLines, setProductLines] = useState<EditorLine[]>([])
  const [materialLines, setMaterialLines] = useState<EditorLine[]>([])

  const [activeReceive, setActiveReceive] = useState<ApiKitchenTransfer | null>(null)
  const [receiveLines, setReceiveLines] = useState<ReceiveLineState[]>([])

  const [pinOpen, setPinOpen] = useState(false)
  const [pinAction, setPinAction] = useState<'dispatch' | 'receive'>('dispatch')
  const [toast, setToast] = useState<string | null>(null)
  const [scanToken, setScanToken] = useState('')
  const [cameraActive, setCameraActive] = useState(true)
  const [isVerifyingScan, setIsVerifyingScan] = useState(false)
  const [manualScanOpen, setManualScanOpen] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const todayLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const reload = useCallback(async () => {
    if (!device?.deviceId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setLoadError(null)
    try {
      const [transfers, peers, delmanList, stock, matStock] = await Promise.all([
        fetchTodayKitchenTransfers(device.deviceId),
        fetchPeerKitchens(device.deviceId),
        fetchKitchenDelmans(device.deviceId),
        fetchKitchenStockLots(device.deviceId),
        fetchKitchenMaterialStock(device.deviceId),
      ])
      setOutgoing(transfers.outgoing)
      setIncoming(transfers.incoming)
      setPeerKitchens(peers.map((p) => ({ id: p.id, name: p.name })))
      setDelmans(delmanList.map((d) => d.name))
      setStockLots(
        stock.map((s) => ({
          productName: s.productName,
          productionDate: s.productionDate,
          stockAvailable: s.stockAvailable,
        })),
      )
      setMaterialStock(matStock)
    } catch {
      setLoadError(kitchenTerms.kitchenTransfer.loadError)
    } finally {
      setLoading(false)
    }
  }, [device?.deviceId])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return undefined

    let cancelled = false
    fetchTodayProductionPlan(device.deviceId)
      .then((plan) => {
        if (cancelled) return
        setPlanCatalog(buildPlanCatalogFromTodayPlan(plan))
      })
      .catch(() => {
        if (!cancelled) setPlanCatalog([])
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  useEffect(() => {
    void reload()
  }, [reload, refreshKey])

  useEffect(() => {
    if (!device?.deviceId) return undefined
    const hasPendingOutgoing = outgoing.some((item) => item.status === 'in_transit')
    const hasPendingIncoming = incoming.some((item) => item.status !== 'received')
    if (!hasPendingOutgoing && !hasPendingIncoming) return undefined
    const timer = window.setInterval(() => void reload(), 5_000)
    return () => window.clearInterval(timer)
  }, [device?.deviceId, outgoing, incoming, reload])

  useEffect(() => {
    const onRefresh = () => void reload()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void reload()
    }
    window.addEventListener('focus', onRefresh)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onRefresh)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [reload])

  const draftOutgoing = useMemo(() => outgoing.filter((item) => item.status === 'draft'), [outgoing])
  const sentOutgoing = useMemo(() => outgoing.filter((item) => item.status !== 'draft'), [outgoing])
  const receivableIncoming = useMemo(
    () => incoming.filter((item) => item.status !== 'received'),
    [incoming],
  )

  const visibleProductLines = useMemo(
    () => filterEditableLines(productLines, showAllSendLines),
    [productLines, showAllSendLines],
  )
  const visibleMaterialLines = useMemo(
    () => filterEditableLines(materialLines, showAllSendLines),
    [materialLines, showAllSendLines],
  )

  const sendPayloadLines = useMemo(() => {
    const products = productLines
      .filter((l) => l.qty > 0)
      .map(({ productName, productionDate, qty }) => ({
        itemType: 'product' as const,
        productName,
        productionDate,
        qty,
      }))
    const materials = materialLines
      .filter((l) => l.qty > 0)
      .map(({ materialName, uom, qty }) => ({
        itemType: 'material' as const,
        materialName,
        uom,
        qty,
      }))
    return [...products, ...materials]
  }, [productLines, materialLines])

  const sendTotalQty = useMemo(
    () => sendPayloadLines.reduce((sum, line) => sum + line.qty, 0),
    [sendPayloadLines],
  )

  const productCategoryGroups = useMemo(
    () =>
      groupProductLinesByCategory(
        visibleProductLines.map((line) => ({
          ...line,
          productName: line.productName ?? '',
        })),
        planCatalog,
      ),
    [visibleProductLines, planCatalog],
  )

  const reviewMaterialLines = useMemo(
    () => sendPayloadLines.filter((l) => l.itemType === 'material'),
    [sendPayloadLines],
  )

  const reviewProductCategoryGroups = useMemo(
    () =>
      groupProductLinesByCategory(
        sendPayloadLines
          .filter((l) => l.itemType === 'product')
          .map((l) => ({ ...l, productName: l.productName ?? '' })),
        planCatalog,
      ),
    [sendPayloadLines, planCatalog],
  )

  const receiveProductCategoryGroups = useMemo(
    () =>
      groupProductLinesByCategory(
        receiveLines
          .filter((line) => line.itemType === 'product')
          .map((line) => ({ ...line, productName: line.productName ?? '' })),
        planCatalog,
      ),
    [receiveLines, planCatalog],
  )

  const receiveMaterialLines = useMemo(
    () => receiveLines.map((line, idx) => ({ line, idx })).filter((x) => x.line.itemType === 'material'),
    [receiveLines],
  )

  function buildEditorLinesFromStock() {
    setProductLines(
      stockLots.map((lot) => ({
        itemType: 'product' as const,
        productName: lot.productName,
        productionDate: lot.productionDate,
        qty: 0,
        maxQty: lot.stockAvailable,
      })),
    )
    setMaterialLines(
      materialStock.map((lot) => ({
        itemType: 'material' as const,
        materialName: lot.materialName,
        uom: lot.uom,
        qty: 0,
        maxQty: lot.stockAvailable,
      })),
    )
  }

  function startNewTransfer() {
    if (peerKitchens.length === 0) {
      setToast(kitchenTerms.kitchenTransfer.toast.noPeerKitchens)
      return
    }
    if (delmans.length === 0) {
      setToast(kitchenTerms.kitchenTransfer.toast.noDelmans)
      return
    }
    const code = newTransferCode()
    setTransferCode(code)
    setTargetKitchen(peerKitchens[0]?.name ?? '')
    setDelmanName(delmans[0] ?? '')
    buildEditorLinesFromStock()
    setSendItemTab('product')
    setShowAllSendLines(false)
    setFormError(null)
    setSendView('edit')
  }

  function openTransferEdit(item: ApiKitchenTransfer) {
    setTransferCode(item.code)
    setTargetKitchen(item.target_kitchen_name ?? '')
    setDelmanName(item.delman_name)
    const prod = item.lines
      .filter((l) => (l.item_type ?? 'product') === 'product')
      .map((l) => {
        const lot = stockLots.find(
          (s) => s.productName === l.product_name && s.productionDate === l.production_date,
        )
        return {
          itemType: 'product' as const,
          productName: l.product_name ?? '',
          productionDate: l.production_date ?? '',
          qty: l.qty,
          maxQty: lot?.stockAvailable ?? l.qty,
        }
      })
    const mat = item.lines
      .filter((l) => l.item_type === 'material')
      .map((l) => {
        const lot = materialStock.find((s) => s.materialName === l.material_name && s.uom === l.uom)
        return {
          itemType: 'material' as const,
          materialName: l.material_name ?? '',
          uom: l.uom ?? 'kg',
          qty: l.qty,
          maxQty: lot?.stockAvailable ?? l.qty,
        }
      })
    setProductLines(
      prod.length > 0
        ? prod
        : stockLots.map((lot) => ({
            itemType: 'product' as const,
            productName: lot.productName,
            productionDate: lot.productionDate,
            qty: 0,
            maxQty: lot.stockAvailable,
          })),
    )
    setMaterialLines(
      mat.length > 0
        ? mat
        : materialStock.map((lot) => ({
            itemType: 'material' as const,
            materialName: lot.materialName,
            uom: lot.uom,
            qty: 0,
            maxQty: lot.stockAvailable,
          })),
    )
    setFormError(null)
    setSendView('edit')
  }

  function validateSendForm(): boolean {
    if (!targetKitchen.trim()) {
      setFormError(kitchenTerms.kitchenTransfer.errors.targetRequired)
      return false
    }
    if (!delmanName.trim()) {
      setFormError(kitchenTerms.kitchenTransfer.errors.delmanRequired)
      return false
    }
    if (sendPayloadLines.length === 0) {
      setFormError(kitchenTerms.kitchenTransfer.errors.minOneLine)
      return false
    }
    setFormError(null)
    return true
  }

  function openSendReview() {
    if (!validateSendForm()) return
    setSendView('review')
  }

  async function handleSaveDraft() {
    if (!device?.deviceId || !validateSendForm()) return
    try {
      await saveKitchenTransfer(transferCode, {
        targetKitchenName: targetKitchen,
        delmanName,
        lines: sendPayloadLines,
      })
      setToast(kitchenTerms.kitchenTransfer.toast.draftSaved)
      setSendView('list')
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : kitchenTerms.kitchenTransfer.toast.saveFailed)
    }
  }

  async function handleDispatch(pin: string) {
    if (!device?.deviceId) return
    try {
      await dispatchKitchenTransfer(transferCode, {
        targetKitchenName: targetKitchen,
        delmanName,
        lines: sendPayloadLines,
        pin,
      })
      setToast(kitchenTerms.kitchenTransfer.toast.dispatched(consumeLastSyncQueued()))
      setPinOpen(false)
      setSendView('list')
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : kitchenTerms.kitchenTransfer.toast.dispatchFailed)
    }
  }

  function openReceiveConfirm(item: ApiKitchenTransfer) {
    setActiveReceive(item)
    setReceiveLines(
      item.lines.map((l) => {
        const isMaterial = l.item_type === 'material'
        return {
          itemType: isMaterial ? ('material' as const) : ('product' as const),
          productName: l.product_name,
          materialName: l.material_name,
          productionDate: l.production_date,
          uom: l.uom,
          planned: l.qty,
          qtyGood: l.qty_good && l.qty_good > 0 ? l.qty_good : l.qty,
        }
      }),
    )
    setFormError(null)
    setReceiveView('confirm')
  }

  const processScanPayload = useCallback(
    async (raw: string) => {
      const payload = raw.trim()
      if (!payload) {
        setToast(kitchenTerms.kitchenTransfer.toast.scanTokenRequired)
        return
      }
      if (!device?.deviceId) return

      setIsVerifyingScan(true)
      setCameraActive(false)
      try {
        const res = await scanKitchenTransferQr(payload)
        const match = incoming.find((i) => i.id === res.transfer_id || i.code === res.transfer_code)
        if (match) {
          openReceiveConfirm(match)
          setScanToken('')
          return
        }
        await reload()
        const refreshed = await fetchTodayKitchenTransfers(device.deviceId)
        const found =
          refreshed.incoming.find((i) => i.id === res.transfer_id) ??
          refreshed.incoming.find((i) => i.code === res.transfer_code)
        if (found) {
          openReceiveConfirm(found)
          setScanToken('')
        } else {
          setToast(kitchenTerms.kitchenTransfer.toast.scanSuccess(res.source_kitchen_name))
          setReceiveView('list')
        }
      } catch (e) {
        setToast(e instanceof Error ? e.message : kitchenTerms.kitchenTransfer.toast.scanInvalid)
        setCameraActive(true)
      } finally {
        setIsVerifyingScan(false)
      }
    },
    [device?.deviceId, incoming, reload],
  )

  async function handleScanSubmit() {
    await processScanPayload(scanToken)
  }

  const handleCameraScan = useCallback(
    (value: string) => {
      if (isVerifyingScan) return
      setScanToken(value.trim())
      void processScanPayload(value)
    },
    [isVerifyingScan, processScanPayload],
  )

  useEffect(() => {
    if (receiveView === 'scan') {
      setCameraActive(true)
      setIsVerifyingScan(false)
    }
  }, [receiveView])

  async function handleReceive(pin: string) {
    if (!activeReceive) return
    try {
      await receiveKitchenTransfer(
        activeReceive.code,
        pin,
        receiveLines.map((l) => ({
          itemType: l.itemType,
          productName: l.productName,
          materialName: l.materialName,
          productionDate: l.productionDate,
          uom: l.uom,
          qtyGood: l.qtyGood,
        })),
      )
      setToast(kitchenTerms.kitchenTransfer.toast.received(consumeLastSyncQueued()))
      setPinOpen(false)
      setReceiveView('list')
      setActiveReceive(null)
      await reload()
    } catch (e) {
      setToast(e instanceof Error ? e.message : kitchenTerms.kitchenTransfer.toast.receiveFailed)
    }
  }

  function setReceiveLine(idx: number, patch: Partial<ReceiveLineState>) {
    setReceiveLines((prev) => prev.map((line, i) => (i === idx ? { ...line, ...patch } : line)))
  }

  function applyReceiveAsPlanned(idx: number) {
    setReceiveLines((prev) =>
      prev.map((line, i) =>
        i === idx ? { ...line, qtyGood: line.planned } : line,
      ),
    )
  }

  const pageTitle =
    tab === 'send'
      ? kitchenTerms.kitchenTransfer.send.title
      : kitchenTerms.kitchenTransfer.receive.title
  const pageSubtitle =
    tab === 'send'
      ? kitchenTerms.kitchenTransfer.send.subtitle
      : kitchenTerms.kitchenTransfer.receive.subtitle

  function renderProductEditorLine(line: EditorLine) {
    const realIdx = productLines.findIndex(
      (l) => l.productName === line.productName && l.productionDate === line.productionDate,
    )
    return (
      <div
        key={`${line.productName}-${line.productionDate}`}
        className="flex items-center justify-between gap-3 border-b border-surface-variant pb-3 last:border-0 last:pb-0"
      >
        <div className="min-w-0 flex-1">
          <p className="text-body-sm font-semibold text-on-surface break-words">{line.productName}</p>
          <p className="text-body-sm text-on-surface-variant">
            {kitchenTerms.kitchenTransfer.editor.productionDate(
              formatProductionDate(line.productionDate ?? ''),
            )}{' '}
            · {kitchenTerms.kitchenTransfer.editor.maxPcs(line.maxQty)}
          </p>
        </div>
        <QtyStepper
          value={line.qty}
          min={0}
          max={line.maxQty}
          disabled={line.maxQty <= 0}
          onChange={(qty) => {
            setProductLines((prev) => prev.map((l, i) => (i === realIdx ? { ...l, qty } : l)))
          }}
        />
      </div>
    )
  }

  function renderReceiveLineCard(line: ReceiveLineState, idx: number) {
    const label =
      line.itemType === 'material'
        ? kitchenTerms.kitchenTransfer.confirm.materialLine(line.materialName ?? '', line.uom ?? '')
        : kitchenTerms.kitchenTransfer.confirm.productLine(
            line.productName ?? '',
            formatProductionDate(line.productionDate ?? ''),
          )
    const unit = line.itemType === 'product' ? 'pcs' : (line.uom ?? '')
    return (
      <div key={`${line.itemType}-${line.productName ?? line.materialName}-${idx}`} className="card space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-body-sm font-semibold text-on-surface break-words">{label}</p>
            <p className="text-body-sm text-on-surface-variant">
              {kitchenTerms.kitchenTransfer.confirm.planned(line.planned, unit)}
            </p>
          </div>
          <button
            type="button"
            className="text-body-sm text-primary font-semibold shrink-0"
            onClick={() => applyReceiveAsPlanned(idx)}
          >
            {kitchenTerms.kitchenTransfer.confirm.matchPlan}
          </button>
        </div>
        <div className="space-y-1">
          <p className="text-body-sm text-on-surface-variant">{kitchenTerms.kitchenTransfer.confirm.received}</p>
          <QtyStepper
            value={line.qtyGood}
            min={0}
            max={line.planned}
            onChange={(qtyGood) => setReceiveLine(idx, { qtyGood })}
          />
        </div>
      </div>
    )
  }

  return (
    <section className="space-y-4 pb-24">
      <div className="card relative space-y-3">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          aria-label={kitchenTerms.kitchenTransfer.refreshAria}
          title={kitchenTerms.kitchenTransfer.refreshAria}
        >
          <span className="material-symbols-outlined">refresh</span>
        </button>

        <Link
          href="/kitchen/home"
          className="text-body-sm text-primary inline-flex items-center gap-1 pr-12"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          {kitchenTerms.kitchenTransfer.backHome}
        </Link>
        <p className="text-headline-md text-primary break-words pr-12">{pageTitle}</p>
        <p className="text-body-sm text-on-surface-variant">{pageSubtitle}</p>
        <p className="text-body-sm text-on-surface-variant capitalize">
          {kitchenName} · {todayLabel}
        </p>
        <div className="pt-3 border-t border-surface-variant">
          <FlowStepper
            steps={tab === 'send' ? SEND_FLOW_STEPS : RECEIVE_FLOW_STEPS}
            activeStep={tab === 'send' ? sendFlowStep(sendView) : receiveFlowStep(receiveView)}
          />
        </div>
      </div>

      <div className="card p-2">
        <div className="grid grid-cols-2 gap-2">
          <Link
            href="/kitchen/kitchen-transfer"
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] text-center ${
              tab === 'send'
                ? 'bg-primary-container text-on-primary-container'
                : 'text-on-surface-variant bg-surface'
            }`}
            onClick={() => {
              setTab('send')
              setSendView('list')
            }}
          >
            {kitchenTerms.kitchenTransfer.tabs.send}
          </Link>
          <Link
            href="/kitchen/kitchen-transfer/receive"
            className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] text-center ${
              tab === 'receive'
                ? 'bg-primary-container text-on-primary-container'
                : 'text-on-surface-variant bg-surface'
            }`}
            onClick={() => {
              setTab('receive')
              setReceiveView('list')
            }}
          >
            {kitchenTerms.kitchenTransfer.tabs.receive}
          </Link>
        </div>
      </div>

      {loading && <SkeletonList count={3} label={kitchenTerms.kitchenTransfer.loading} />}

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {kitchenTerms.kitchenTransfer.retry}
          </button>
        </div>
      )}

      {!loading && !loadError && tab === 'send' && sendView === 'list' && (
        <div className="space-y-4">
          {draftOutgoing.length > 0 && (
            <div className="space-y-2">
              <p className="text-body-sm font-semibold text-on-surface-variant">
                {kitchenTerms.kitchenTransfer.sendList.draftSection}
              </p>
              {draftOutgoing.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="card w-full text-left hover:border-primary transition-colors"
                  onClick={() => openTransferEdit(item)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface">→ {item.target_kitchen_name}</p>
                      <p className="text-body-sm text-on-surface-variant mt-1">
                        {formatTransferSummary(item.lines)} · {item.delman_name}
                      </p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={transferStatusBadge(item.status)}>{transferStatusLabel(item.status)}</span>
                  </div>
                  <p className="text-body-sm text-primary font-semibold mt-2">
                    {kitchenTerms.kitchenTransfer.sendList.continueEdit}
                  </p>
                </button>
              ))}
            </div>
          )}

          {sentOutgoing.length > 0 && (
            <div className="space-y-2">
              <p className="text-body-sm font-semibold text-on-surface-variant">
                {kitchenTerms.kitchenTransfer.sendList.sentSection}
              </p>
              {sentOutgoing.map((item) => (
                <div key={item.id} className="card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-lg font-semibold text-on-surface">→ {item.target_kitchen_name}</p>
                      <p className="text-body-sm text-on-surface-variant mt-1">
                        {formatTransferSummary(item.lines)} · {item.delman_name}
                      </p>
                    </div>
                    <span className={transferStatusBadge(item.status)}>{transferStatusLabel(item.status)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {outgoing.length === 0 && (
            <EmptyState
              icon="swap_horiz"
              title={kitchenTerms.kitchenTransfer.sendList.emptyTitle}
              description={kitchenTerms.kitchenTransfer.sendList.emptyDescription}
            />
          )}

          <button type="button" className="btn-primary w-full min-h-[44px] text-center" onClick={startNewTransfer}>
            {kitchenTerms.kitchenTransfer.sendList.newTransfer}
          </button>
        </div>
      )}

      {!loading && !loadError && tab === 'send' && sendView === 'edit' && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <p className="text-headline-md text-primary break-words">
              {kitchenTerms.kitchenTransfer.editor.title(targetKitchen || '…')}
            </p>
            <p className="text-body-sm font-mono text-on-surface-variant">{transferCode}</p>

            <div className="rounded-lg bg-surface-container p-3 text-body-sm">
              <p className="text-on-surface-variant">{kitchenTerms.kitchenTransfer.stock.availableLabel}</p>
              <p className="text-on-surface mt-1 break-words">{stockSummaryText(stockLots)}</p>
            </div>

            <div className="space-y-2">
              <label className="field-label" htmlFor="kt_target_kitchen">
                {kitchenTerms.kitchenTransfer.editor.targetKitchen}
              </label>
              <select
                id="kt_target_kitchen"
                className="input-field"
                value={targetKitchen}
                onChange={(e) => setTargetKitchen(e.target.value)}
              >
                {peerKitchens.map((k) => (
                  <option key={k.id} value={k.name}>
                    {k.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="field-label" htmlFor="kt_delman">
                {kitchenTerms.kitchenTransfer.editor.delman}
              </label>
              <select
                id="kt_delman"
                className="input-field"
                value={delmanName}
                onChange={(e) => setDelmanName(e.target.value)}
              >
                {delmans.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card p-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
                  sendItemTab === 'product'
                    ? 'bg-secondary-container text-on-secondary-container'
                    : 'text-on-surface-variant bg-surface'
                }`}
                onClick={() => setSendItemTab('product')}
              >
                {kitchenTerms.kitchenTransfer.editor.tabs.product}
              </button>
              <button
                type="button"
                className={`rounded-lg px-3 py-2 text-body-sm font-semibold min-h-[44px] ${
                  sendItemTab === 'material'
                    ? 'bg-secondary-container text-on-secondary-container'
                    : 'text-on-surface-variant bg-surface'
                }`}
                onClick={() => setSendItemTab('material')}
              >
                {kitchenTerms.kitchenTransfer.editor.tabs.material}
              </button>
            </div>
          </div>

          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-body-sm text-on-surface-variant">
                {sendItemTab === 'product'
                  ? kitchenTerms.kitchenTransfer.editor.productQtyLabel
                  : kitchenTerms.kitchenTransfer.editor.materialQtyLabel}
              </p>
              <button
                type="button"
                className="text-body-sm text-primary font-semibold shrink-0"
                onClick={() => setShowAllSendLines((prev) => !prev)}
              >
                {showAllSendLines
                  ? kitchenTerms.kitchenTransfer.editor.hideZero
                  : kitchenTerms.kitchenTransfer.editor.showAll}
              </button>
            </div>

            {sendItemTab === 'product' && visibleProductLines.length === 0 && (
              <EmptyState
                compact
                icon="inventory_2"
                title={kitchenTerms.kitchenTransfer.editor.emptyProductTitle}
                description={kitchenTerms.kitchenTransfer.editor.emptyProductDescription}
              />
            )}

            {sendItemTab === 'product' && productCategoryGroups.length > 0 && (
              <div className="space-y-2">
                {productCategoryGroups.map((category) => {
                  const categoryQty = category.items.reduce((sum, line) => sum + line.qty, 0)
                  return (
                    <CollapsibleCategoryBlock
                      key={category.categoryName}
                      categoryName={category.categoryName}
                      itemCount={category.items.length}
                      defaultOpen
                      summary={kitchenTerms.kitchenTransfer.review.pcs(categoryQty)}
                    >
                      <div className="space-y-3 p-2">
                        {category.items.map((line) => renderProductEditorLine(line))}
                      </div>
                    </CollapsibleCategoryBlock>
                  )
                })}
              </div>
            )}

            {sendItemTab === 'material' && visibleMaterialLines.length === 0 && (
              <p className="text-body-sm text-on-surface-variant">
                {kitchenTerms.kitchenTransfer.editor.emptyMaterial}
              </p>
            )}

            {sendItemTab === 'material' &&
              visibleMaterialLines.map((line) => {
                const realIdx = materialLines.findIndex(
                  (l) => l.materialName === line.materialName && l.uom === line.uom,
                )
                return (
                  <div
                    key={`${line.materialName}-${line.uom}`}
                    className="flex items-center justify-between gap-3 border-b border-surface-variant pb-3 last:border-0 last:pb-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-body-sm font-semibold text-on-surface break-words">
                        {line.materialName}
                      </p>
                      <p className="text-body-sm text-on-surface-variant">
                        {kitchenTerms.kitchenTransfer.editor.maxUom(line.maxQty, line.uom ?? '')}
                      </p>
                    </div>
                    <QtyStepper
                      value={line.qty}
                      min={0}
                      max={line.maxQty}
                      disabled={line.maxQty <= 0}
                      onChange={(qty) => {
                        setMaterialLines((prev) =>
                          prev.map((l, i) => (i === realIdx ? { ...l, qty } : l)),
                        )
                      }}
                    />
                  </div>
                )
              })}
          </div>

          {formError && (
            <div role="alert" className="alert-error">
              <span>{formError}</span>
            </div>
          )}

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setSendView('list')}>
              {kitchenTerms.kitchenTransfer.editor.cancel}
            </button>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => void handleSaveDraft()}>
              {kitchenTerms.kitchenTransfer.editor.save}
            </button>
            <button type="button" className="btn-primary flex-1 min-h-[44px] text-center" onClick={openSendReview}>
              {kitchenTerms.kitchenTransfer.editor.continue}
            </button>
          </StickyActionFooter>
        </div>
      )}

      {!loading && !loadError && tab === 'send' && sendView === 'review' && (
        <div className="space-y-4">
          <div className="card space-y-2">
            <p className="text-headline-md text-primary">{kitchenTerms.kitchenTransfer.review.title}</p>
            <p className="text-body-sm text-on-surface-variant">
              {kitchenTerms.kitchenTransfer.review.destination(targetKitchen, delmanName)}
            </p>
          </div>

          {reviewProductCategoryGroups.length > 0 && (
            <div className="space-y-2">
              {reviewProductCategoryGroups.map((category) => {
                const categoryQty = category.items.reduce((sum, line) => sum + line.qty, 0)
                return (
                  <CollapsibleCategoryBlock
                    key={category.categoryName}
                    categoryName={category.categoryName}
                    itemCount={category.items.length}
                    defaultOpen
                    summary={kitchenTerms.kitchenTransfer.review.pcs(categoryQty)}
                  >
                    <div className="rounded-lg border border-outline-variant divide-y divide-surface-variant mx-2 mb-2">
                      {category.items.map((line, index) => (
                        <div
                          key={`${line.productName}-${line.productionDate}-${index}`}
                          className="flex items-center justify-between gap-3 p-3"
                        >
                          <div className="min-w-0">
                            <p className="text-body-lg font-semibold text-on-surface break-words">
                              {line.productName}
                            </p>
                            <p className="text-body-sm text-on-surface-variant">
                              {kitchenTerms.kitchenTransfer.editor.productionDate(
                                formatProductionDate(line.productionDate ?? ''),
                              )}
                            </p>
                          </div>
                          <p className="text-body-lg font-bold text-primary shrink-0">
                            {kitchenTerms.kitchenTransfer.review.pcs(line.qty)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CollapsibleCategoryBlock>
                )
              })}
            </div>
          )}

          {reviewMaterialLines.length > 0 && (
            <ActionSummaryCard
              items={reviewMaterialLines.map((line, index) => ({
                id: `material-${index}`,
                title: kitchenTerms.kitchenTransfer.confirm.materialLine(
                  line.materialName ?? '',
                  line.uom ?? '',
                ),
                subtitle: kitchenTerms.kitchenTransfer.review.materialSubtitle,
                value: `${line.qty} ${line.uom ?? ''}`,
              }))}
              totalLabel={kitchenTerms.kitchenTransfer.editor.tabs.material}
              totalValue={kitchenTerms.kitchenTransfer.review.pcs(
                reviewMaterialLines.reduce((sum, line) => sum + line.qty, 0),
              )}
            />
          )}

          <div className="rounded-lg bg-surface-container p-3 flex items-center justify-between">
            <span className="text-body-sm text-on-surface-variant">
              {kitchenTerms.kitchenTransfer.review.totalLabel}
            </span>
            <span className="text-body-lg font-bold text-on-surface">
              {kitchenTerms.kitchenTransfer.review.totalValue(
                sendPayloadLines.length,
                sendTotalQty,
              )}
            </span>
          </div>

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setSendView('edit')}>
              {kitchenTerms.kitchenTransfer.review.back}
            </button>
            <button
              type="button"
              className="btn-primary flex-1 min-h-[44px] text-center"
              onClick={() => {
                setPinAction('dispatch')
                setPinOpen(true)
              }}
            >
              {kitchenTerms.kitchenTransfer.review.handover}
            </button>
          </StickyActionFooter>
        </div>
      )}

      {!loading && !loadError && tab === 'receive' && receiveView === 'list' && (
        <div className="space-y-4">
          <button
            type="button"
            className="btn-primary w-full min-h-[44px] inline-flex items-center justify-center gap-2"
            onClick={() => {
              setScanToken('')
              setManualScanOpen(false)
              setReceiveView('scan')
            }}
          >
            <span className="material-symbols-outlined">qr_code_scanner</span>
            {kitchenTerms.kitchenTransfer.receiveList.scanCta}
          </button>

          {receivableIncoming.length === 0 ? (
            <EmptyState
              icon="move_to_inbox"
              title={kitchenTerms.kitchenTransfer.receiveList.emptyTitle}
              description={kitchenTerms.kitchenTransfer.receiveList.emptyDescription}
            />
          ) : (
            <div className="space-y-2">
              <p className="text-body-sm font-semibold text-on-surface-variant">
                {kitchenTerms.kitchenTransfer.receiveList.pendingSection}
              </p>
              {incoming.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="card w-full text-left hover:border-primary transition-colors disabled:opacity-60"
                  onClick={() => {
                    if (item.receivable) openReceiveConfirm(item)
                    else setToast(kitchenTerms.kitchenTransfer.toast.scanBeforeReceive)
                  }}
                  disabled={item.status === 'received'}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-body-lg font-semibold text-on-surface break-words">
                        {kitchenTerms.kitchenTransfer.receiveList.fromKitchen(
                          item.source_kitchen_name ?? '',
                        )}
                      </p>
                      <p className="text-body-sm text-on-surface-variant mt-1">
                        {formatTransferSummary(item.lines)} · {item.delman_name}
                      </p>
                      <p className="text-label-md font-mono text-on-surface-variant mt-1">{item.code}</p>
                    </div>
                    <span className={transferStatusBadge(item.status)}>{transferStatusLabel(item.status)}</span>
                  </div>
                  {!item.receivable && item.status !== 'received' && (
                    <p className="text-body-sm text-status-warning mt-2">
                      {kitchenTerms.kitchenTransfer.receiveList.needsScan}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'receive' && receiveView === 'scan' && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <div>
              <p className="text-headline-md text-primary">{kitchenTerms.kitchenTransfer.scan.title}</p>
              <p className="text-body-sm text-on-surface-variant mt-1">
                {kitchenTerms.kitchenTransfer.scan.description}
              </p>
            </div>

            <div className="flex items-center justify-between gap-2">
              <p className="text-body-sm font-semibold text-on-surface">Scan kamera</p>
              {cameraActive && !isVerifyingScan && (
                <span className="badge-warning text-label-md">
                  {kitchenTerms.kitchenTransfer.scan.liveBadge}
                </span>
              )}
            </div>

            <QrCameraScanner
              active={cameraActive && !isVerifyingScan}
              inactiveMessage={
                isVerifyingScan
                  ? kitchenTerms.kitchenTransfer.scan.verifying
                  : kitchenTerms.kitchenTransfer.scan.cameraHint
              }
              onScan={handleCameraScan}
              unsupportedMessage={kitchenTerms.kitchenTransfer.scan.cameraUnsupported}
              permissionDeniedMessage={kitchenTerms.kitchenTransfer.scan.cameraPermissionDenied}
              scanningHint={kitchenTerms.kitchenTransfer.scan.cameraHint}
            />

            <details
              className="rounded-lg border border-outline-variant/60 bg-surface-container-low"
              open={manualScanOpen}
              onToggle={(e) => setManualScanOpen((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer select-none px-3 py-3 text-body-sm font-medium min-h-[44px] flex items-center gap-2">
                <span className="material-symbols-outlined text-lg text-on-surface-variant">
                  keyboard
                </span>
                {kitchenTerms.kitchenTransfer.scan.manualToggle}
              </summary>
              <div className="space-y-3 border-t border-outline-variant/40 px-3 pb-3 pt-2">
                <label className="field-label" htmlFor="kt_scan_token">
                  {kitchenTerms.kitchenTransfer.scan.tokenLabel}
                </label>
                <input
                  id="kt_scan_token"
                  className="input-field font-mono"
                  value={scanToken}
                  onChange={(e) => setScanToken(e.target.value.trim())}
                  placeholder={kitchenTerms.kitchenTransfer.scan.tokenPlaceholder}
                  autoComplete="off"
                  spellCheck={false}
                  disabled={isVerifyingScan}
                />
                <button
                  type="button"
                  className="btn-primary w-full min-h-[48px] text-center disabled:opacity-40"
                  disabled={isVerifyingScan || !scanToken.trim()}
                  onClick={() => void handleScanSubmit()}
                >
                  {kitchenTerms.kitchenTransfer.scan.verify}
                </button>
              </div>
            </details>
          </div>

          <button
            type="button"
            className="btn-outline w-full min-h-[44px]"
            onClick={() => setReceiveView('list')}
            disabled={isVerifyingScan}
          >
            {kitchenTerms.kitchenTransfer.scan.cancel}
          </button>
        </div>
      )}

      {tab === 'receive' && receiveView === 'confirm' && activeReceive && (
        <div className="space-y-4">
          <div className="card space-y-2">
            <p className="text-headline-md text-primary break-words">
              {kitchenTerms.kitchenTransfer.confirm.title(activeReceive.source_kitchen_name ?? '')}
            </p>
            <p className="text-body-sm text-on-surface-variant">
              {kitchenTerms.kitchenTransfer.confirm.meta(
                activeReceive.code,
                activeReceive.delman_name,
              )}
            </p>
            <p className="text-body-sm text-on-surface-variant">
              {kitchenTerms.kitchenTransfer.confirm.hint}
            </p>
          </div>

          {receiveProductCategoryGroups.length > 0 && (
            <div className="space-y-2">
              {receiveProductCategoryGroups.map((category) => (
                <CollapsibleCategoryBlock
                  key={category.categoryName}
                  categoryName={category.categoryName}
                  itemCount={category.items.length}
                  defaultOpen
                >
                  <div className="space-y-2 p-2">
                    {category.items.map((line) => {
                      const idx = receiveLines.findIndex(
                        (r) =>
                          r.itemType === 'product' &&
                          r.productName === line.productName &&
                          r.productionDate === line.productionDate,
                      )
                      if (idx < 0) return null
                      return renderReceiveLineCard(receiveLines[idx], idx)
                    })}
                  </div>
                </CollapsibleCategoryBlock>
              ))}
            </div>
          )}

          {receiveMaterialLines.map(({ line, idx }) => renderReceiveLineCard(line, idx))}

          <StickyActionFooter>
            <button type="button" className="btn-outline flex-1 min-h-[44px]" onClick={() => setReceiveView('list')}>
              {kitchenTerms.kitchenTransfer.confirm.cancel}
            </button>
            <button
              type="button"
              className="btn-primary flex-1 min-h-[44px] text-center"
              onClick={() => {
                setPinAction('receive')
                setPinOpen(true)
              }}
            >
              {kitchenTerms.kitchenTransfer.confirm.submit}
            </button>
          </StickyActionFooter>
        </div>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={
          pinAction === 'dispatch'
            ? kitchenTerms.kitchenTransfer.pin.dispatch.title
            : kitchenTerms.kitchenTransfer.pin.receive.title
        }
        description={
          pinAction === 'dispatch'
            ? kitchenTerms.kitchenTransfer.pin.dispatch.description
            : kitchenTerms.kitchenTransfer.pin.receive.description
        }
        confirmLabel={
          pinAction === 'dispatch'
            ? kitchenTerms.kitchenTransfer.pin.dispatch.confirm
            : kitchenTerms.kitchenTransfer.pin.receive.confirm
        }
        onClose={() => setPinOpen(false)}
        onConfirm={async (pin) => {
          if (pinAction === 'dispatch') await handleDispatch(pin)
          else await handleReceive(pin)
        }}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

      {!useApi && (
        <p className="text-caption text-on-surface-variant">{kitchenTerms.kitchenTransfer.mockHint}</p>
      )}
    </section>
  )
}
